<template>
  <div class="page-inventory">
    <div class="columns is-multiline">
      <div class="column is-12">
        <h1 class="title">Inventory</h1>
      </div>

      <!-- Search + Filter buttons -->
      <div class="column is-12">
        <div class="field has-addons">
          <div class="control is-expanded">
            <input
              v-model="search"
              @keyup.enter="fetchItems"
              type="search"
              class="input"
              placeholder="Search by name or description…"
            />
          </div>
          <div class="control">
            <button class="button is-info" @click="showFilters = true">
              <span class="icon"><i class="fas fa-filter"></i></span>
              <span>Filters</span>
            </button>
          </div>
          <div class="control">
            <button
              class="button is-light"
              :disabled="!hasActiveFilters"
              @click="clearFilters"
            >
              <span class="icon"><i class="fas fa-times"></i></span>
              <span>Clear</span>
            </button>
          </div>
        </div>

        <!-- Active filters summary -->
        <div v-if="hasActiveFilters" class="tags mt-2">
          <span
            v-for="(val, key) in activeFilterLabels"
            :key="key"
            class="tag is-info"
          >
            {{ val }}
            <button class="delete is-small" @click="removeFilter(key)"></button>
          </span>
        </div>
      </div>

      <!-- Items grid -->
      <div class="column is-12">
        <div class="columns is-multiline is-variable is-3">
          <div
            v-for="item in items"
            :key="item.id"
            class="column is-12-tablet is-6-desktop is-4-widescreen"
          >
            <div class="card">
              <div class="card-content">
                <div class="media">
                  <div class="media-content">
                    <p class="title is-6 mb-1">{{ item.name }}</p>
                    <p
                      v-if="item.description"
                      class="subtitle is-7 has-text-grey"
                    >
                      {{ item.description }}
                    </p>
                  </div>
                </div>

                <div class="tags mb-2">
                  <span
                    :class="[
                      'tag',
                      item.type === 'product' ? 'is-link' : 'is-info',
                    ]"
                  >
                    {{ item.type_display || item.type }}
                  </span>
                  <span
                    v-if="item.type !== 'service'"
                    :class="['tag', statusTag(item)]"
                  >
                    {{ statusText(item) }}
                  </span>
                </div>

                <div class="columns is-mobile is-gapless">
                  <div class="column">
                    <p class="is-size-7 has-text-grey">Stock</p>
                    <p class="is-size-6">{{ item.current_stock ?? '—' }}</p>
                  </div>
                  <div class="column has-text-right">
                    <p class="is-size-7 has-text-grey">Min</p>
                    <p class="is-size-6">{{ item.minimum_stock }}</p>
                  </div>
                </div>

                <div
                  class="is-flex is-justify-content-space-between is-align-items-center mt-2"
                >
                  <span class="is-size-7 has-text-grey">Unit price</span>
                  <strong>{{ formatCurrency(item.unit_price) }}</strong>
                </div>

                <div class="buttons mt-3">
                  <router-link
                    :to="{ name: 'ItemDetail', params: { id: item.id } }"
                    class="button is-small is-light"
                  >
                    Details
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <!-- No results -->
          <div v-if="!loading && items.length === 0" class="column is-12">
            <div class="notification is-light has-text-centered">
              No items found.
            </div>
          </div>
        </div>
      </div>

      <!-- Error message -->
      <div class="column is-12" v-if="error">
        <div class="notification is-danger is-light">
          <button class="delete" @click="error = ''"></button>
          {{ error }}
        </div>
      </div>
    </div>

    <!-- Filters Modal -->
    <div class="modal" :class="{ 'is-active': showFilters }">
      <div class="modal-background" @click="showFilters = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Filters</p>
          <button class="delete" @click="showFilters = false"></button>
        </header>
        <section class="modal-card-body">
          <!-- Active/Archived -->
          <div class="field">
            <label class="label">Status</label>
            <div class="select is-fullwidth">
              <select v-model="filters.is_active">
                <option :value="null">All</option>
                <option :value="true">Active</option>
                <option :value="false">Archived</option>
              </select>
            </div>
          </div>

          <!-- Type -->
          <div class="field">
            <label class="label">Type</label>
            <div class="select is-fullwidth">
              <select v-model="filters.type">
                <option value="">All</option>
                <option value="product">Product</option>
                <option value="service">Service</option>
              </select>
            </div>
          </div>

          <!-- Price -->
          <div class="field">
            <label class="label">Unit price (EUR)</label>
            <div class="field is-grouped">
              <div class="control is-expanded">
                <input
                  v-model.number="filters.price_min"
                  type="number"
                  class="input"
                  placeholder="Min"
                />
              </div>
              <div class="control is-expanded">
                <input
                  v-model.number="filters.price_max"
                  type="number"
                  class="input"
                  placeholder="Max"
                />
              </div>
            </div>
          </div>

          <!-- Stock low -->
          <div class="field">
            <label class="checkbox">
              <input type="checkbox" v-model="filters.low_stock" />
              Show only low stock items
            </label>
          </div>

          <!-- Date created -->
          <div class="field">
            <label class="label">Date created</label>
            <div class="field is-grouped">
              <div class="control is-expanded">
                <input
                  v-model="filters.date_from"
                  type="date"
                  class="input"
                  placeholder="From"
                />
              </div>
              <div class="control is-expanded">
                <input
                  v-model="filters.date_to"
                  type="date"
                  class="input"
                  placeholder="To"
                />
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-success" @click="applyFilters">Apply</button>
          <button class="button" @click="showFilters = false">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/utils/axios';

export default {
  name: 'InventoryView',
  data() {
    return {
      items: [],
      loading: false,
      error: '',
      search: '',
      showFilters: false,
      filters: {
        is_active: null,
        type: '',
        price_min: null,
        price_max: null,
        low_stock: false,
        date_from: '',
        date_to: '',
      },
    };
  },
  created() {
    this.fetchItems();
  },
  computed: {
    hasActiveFilters() {
      return (
        this.search ||
        this.filters.is_active !== null ||
        this.filters.type ||
        this.filters.price_min !== null ||
        this.filters.price_max !== null ||
        this.filters.low_stock ||
        this.filters.date_from ||
        this.filters.date_to
      );
    },
    activeFilterLabels() {
      const labels = {};
      if (this.filters.is_active === true) labels.is_active = 'Active';
      if (this.filters.is_active === false) labels.is_active = 'Archived';
      if (this.filters.type) labels.type = this.filters.type;
      if (this.filters.price_min !== null)
        labels.price_min = `Min €${this.filters.price_min}`;
      if (this.filters.price_max !== null)
        labels.price_max = `Max €${this.filters.price_max}`;
      if (this.filters.low_stock) labels.low_stock = 'Low stock';
      if (this.filters.date_from)
        labels.date_from = `From ${this.filters.date_from}`;
      if (this.filters.date_to) labels.date_to = `To ${this.filters.date_to}`;
      if (this.search) labels.search = `Search: "${this.search}"`;
      return labels;
    },
  },
  methods: {
    async fetchItems() {
      this.loading = true;
      this.error = '';
      try {
        const params = {};
        if (this.search) params.search = this.search;
        if (this.filters.is_active !== null)
          params.is_active = this.filters.is_active;
        if (this.filters.type) params.type = this.filters.type;
        if (this.filters.price_min !== null)
          params.price_min = this.filters.price_min;
        if (this.filters.price_max !== null)
          params.price_max = this.filters.price_max;
        if (this.filters.low_stock) params.low_stock = true;
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;

        const res = await api.get('/items/', { params });
        const data = res.data;
        this.items = Array.isArray(data) ? data : data.results || data;
      } catch (e) {
        this.error =
          e?.response?.data?.detail || e?.message || 'Failed to fetch items.';
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.showFilters = false;
      this.fetchItems();
    },
    clearFilters() {
      this.search = '';
      this.filters = {
        is_active: null,
        type: '',
        price_min: null,
        price_max: null,
        low_stock: false,
        date_from: '',
        date_to: '',
      };
      this.fetchItems();
    },
    removeFilter(key) {
      if (key in this.filters) {
        if (typeof this.filters[key] === 'boolean') {
          this.filters[key] = null; // ✅ reset au lieu de basculer
        } else if (
          typeof this.filters[key] === 'number' ||
          this.filters[key] === null
        ) {
          this.filters[key] = null;
        } else {
          this.filters[key] = '';
        }
      }
      if (key === 'search') {
        this.search = '';
      }
      this.fetchItems();
    },
    formatCurrency(v) {
      const num = typeof v === 'string' ? Number(v) : v;
      if (Number.isNaN(num)) return v;
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: 'EUR',
        }).format(num);
      } catch (_) {
        return num;
      }
    },
    statusTag(item) {
      if (item.type === 'service') return 'is-light';
      const q = item.current_stock ?? 0;
      if (q <= 0) return 'is-danger';
      if (item.minimum_stock && q <= item.minimum_stock) return 'is-warning';
      return 'is-success';
    },
    statusText(item) {
      if (item.type === 'service') return 'N/A';
      const q = item.current_stock ?? 0;
      if (q <= 0) return 'Out';
      if (item.minimum_stock && q <= item.minimum_stock) return 'Low';
      return 'OK';
    },
  },
};
</script>
